// Анкета «Образование за рубежом» — формулировки и структура.
//
// Тексты взяты из ТЗ ДОСЛОВНО. Идентификаторы вопросов (q_*) стабильны: по
// ним хранятся ответы в edu_profiles.answers, и переписанный текст вопроса
// их не двигает. Менять id нельзя даже когда формулировка изменится.
//
// Переводов на азербайджанский и английский здесь нет намеренно —
// формулировки ещё устаканиваются, переводить будем один раз.

import {
  birthMonthOf,
  DIM_GROUPS,
  dimGroupOf,
  dimScoreOf,
  SCHOOL_SYSTEMS,
  BUDGET_CURRENCIES,
  BUDGET_RANGES,
  COUNTRY_CHOICES,
  countryChoices,
  type CountryRefRow,
  DIET_CHOICES,
  ENTRANCE_EXAMS,
  HEALTH_CHOICES,
  HOUSING_CHOICES,
  HSK_CODE,
  HSK_LEVELS,
  hskLevelOf,
  INTAKE_YEARS,
  LANGUAGE_CERTIFICATES,
  LANGUAGE_LEVELS,
  PRIOR_STUDY,
  STUDY_FORMATS,
  MAIN_WORRY_CHOICES,
  STUDY_LANGUAGES,
  labelOf,
  labelsOf,
  optionOf,
  toId,
  type AnswerOption,
} from "./answer-values";
import { directionName } from "./direction-names";
import { PROGRAM_CLUSTER_IDS } from "./programs";

export type QuestionType =
  | "radio"
  | "multi"
  | "text"
  | "gpa"
  | "month_year";

export type Question = {
  id: string;
  text: string;
  type: QuestionType;
  /**
   * Вариант — либо строка (тогда подпись сама себе идентификатор: так можно
   * для ответов, которые только показываются), либо пара id/подпись из
   * answer-values.ts — обязательно для всего, что ИНТЕРПРЕТИРУЕТСЯ.
   */
  options?: ReadonlyArray<string | AnswerOption>;
  /** Вариант «другое» с полем ввода. */
  withOther?: boolean;
  optional?: boolean;
  hint?: string;
  /** Показывать только если функция вернула true. */
  showIf?: (answers: Answers) => boolean;
  /** multi: не больше стольких вариантов (остальные выключаются, пока не снят выбор). */
  maxSelect?: number;
  /** multi: id вариантов, вынесенных заметно — первыми и отдельной строкой. */
  featured?: readonly string[];
};

export type Block = {
  id: string;
  title: string;
  /** Подзаголовок блока — в ТЗ он есть у денег и ограничений. */
  subtitle?: string;
  questions: Question[];
  /** Блок можно пропустить кнопкой (только деньги). */
  skippable?: boolean;
  skipLabel?: string;
};

/** Привести вариант к паре id/подпись. */
export function normOption(o: string | AnswerOption): AnswerOption {
  return typeof o === "string" ? { id: o, label: o } : o;
}

/** Варианты вопроса в нормализованном виде — для отрисовки. */
export function optionsOf(q: Question): AnswerOption[] {
  return (q.options ?? []).map(normOption);
}

export type AnswerValue = string | string[] | null;
export type Answers = Record<string, AnswerValue>;

export const CONSENT = {
  title: "Прежде чем начать",
  text:
    "Мы сохраним твои ответы, чтобы составить отчёт и показать его тебе и родителям. Данные " +
    "хранятся в твоём кабинете, ты можешь удалить их в любой момент. Мы не передаём их никому без " +
    "отдельного разрешения.",
  checkbox: "Согласен(на), продолжаем",
  note:
    "Ответы лучше давать вместе с родителями — несколько вопросов про деньги и здоровье без них " +
    "не ответить.",
} as const;

export const DONE = {
  text: "Готово. Мы посчитаем варианты и покажем отчёт — это займёт меньше минуты.",
  budgetSkipped:
    "Ты пропустил(а) вопросы про бюджет. Отчёт покажет варианты всех уровней стоимости — " +
    "обсуди их с родителями.",
} as const;

const has = (a: AnswerValue, ...values: string[]) =>
  Array.isArray(a) ? a.some((v) => values.includes(v)) : values.includes(String(a ?? ""));

// Вопрос про сертификат отвечен и это не «нет» — тогда спрашиваем балл и дату.
const hasCertificate = (answers: Answers) =>
  optionOf(LANGUAGE_CERTIFICATES, answers.q_lang_certificate)?.hasCertificate === true;

// HSK меряется уровнем, а не баллом: вместо «Какой балл?» — уровень
// вариантом и балл внутри уровня отдельным полем. Свободное поле здесь
// приняло бы и «4», и «210», и движок не отличил бы одно от другого.
const isHsk = (answers: Answers) =>
  toId(LANGUAGE_CERTIFICATES, answers.q_lang_certificate) === HSK_CODE;
const hasScoredCertificate = (answers: Answers) => hasCertificate(answers) && !isHsk(answers);

/** Отмечен ли конкретный экзамен в q_exams_taken. */
/** Отмечен ли конкретный экзамен. Сверка по id варианта, не по подписи. */
const examTaken = (examId: string) => (answers: Answers) => {
  const v = answers.q_exams_taken;
  return Array.isArray(v) && v.some((x) => toId(ENTRANCE_EXAMS, x) === examId);
};

/**
 * Год вуза дома в том виде, в каком его читает движок: булево.
 *
 * «Учусь сейчас, первый курс закончу до поступления» — это ДА: к моменту
 * подачи год будет за плечами, а ради этого вопрос и задан. «Бросил(а) до
 * конца года» — нет: барьер снимает законченный курс, а не начатый.
 */
export function priorStudyYearFrom(answer: AnswerValue): boolean | undefined {
  const o = optionOf(PRIOR_STUDY, answer);
  return o ? o.hasYear : undefined;
}

/**
 * Медицина и туризм — заметно, первыми (17.10.2026): их чаще всего ищут, а в
 * общем списке из 23 направлений они теряются. Подписи — короче названий
 * кластеров, на трёх языках (экран берёт их в questionnaire-i18n.ts).
 */
export const MEDICINE_CLUSTER = "healthcare";
export const FEATURED_DIRECTIONS = [MEDICINE_CLUSTER, "hospitality"] as const;
export const FEATURED_DIRECTION_LABELS: Record<(typeof FEATURED_DIRECTIONS)[number], Record<"ru" | "az" | "en", string>> = {
  healthcare: { ru: "Медицина", az: "Tibb", en: "Medicine" },
  hospitality: { ru: "Туризм и гостеприимство", az: "Turizm və qonaqpərvərlik", en: "Tourism and hospitality" },
};

const knowsCareer = (a: Answers) => has(a.q_career_known, "да, знаю точно", "есть пара вариантов");
const picked = (a: Answers, id: string) => Array.isArray(a.q_career_pick) && a.q_career_pick.includes(id);

/**
 * Первый блок при непройденном карьерном тесте. Знает, кем хочет быть, —
 * выбирает 1–2 направления из того же справочника кластеров, что после теста
 * (17.10.2026: раньше было свободное поле, и движок его не читал). Слова —
 * дополнением. Медицина — ещё оценки по биологии и химии.
 */
const BLOCK_1_WITHOUT_SNAPSHOT: Question[] = [
  {
    id: "q_career_known",
    text: "Ты уже понимаешь, чем хочешь заниматься после учёбы?",
    type: "radio",
    options: ["да, знаю точно", "есть пара вариантов", "совсем не знаю"],
  },
  {
    id: "q_career_pick",
    text: "Выбери одно или два направления",
    type: "multi",
    maxSelect: 2,
    featured: FEATURED_DIRECTIONS,
    options: [
      ...FEATURED_DIRECTIONS.map((id) => ({ id, label: FEATURED_DIRECTION_LABELS[id].ru })),
      ...PROGRAM_CLUSTER_IDS.filter((id) => !(FEATURED_DIRECTIONS as readonly string[]).includes(id)).map((id) => ({ id, label: directionName(id) })),
    ],
    showIf: knowsCareer,
  },
  {
    id: "q_career_field",
    text: "Если хочешь, уточни словами",
    type: "text",
    optional: true,
    showIf: knowsCareer,
  },
  {
    id: "q_grade_biology",
    text: "Оценка по биологии",
    type: "text",
    hint: "Большинство медвузов проверяют биологию и химию",
    showIf: (a) => knowsCareer(a) && picked(a, MEDICINE_CLUSTER),
  },
  {
    id: "q_grade_chemistry",
    text: "Оценка по химии",
    type: "text",
    showIf: (a) => knowsCareer(a) && picked(a, MEDICINE_CLUSTER),
  },
];

/**
 * Тот же блок, когда карьерный тест пройден: два вопроса выше заменяются
 * одним. Варианты подставляются из снимка — поэтому это функция, а не
 * константа.
 */
export function careerConfirmQuestion(directions: string[]): Question {
  return {
    id: "q_career_confirm",
    text:
      "По результатам твоего теста тебе подходят эти направления. Какие из них ты рассматриваешь " +
      "всерьёз?",
    type: "multi",
    // Подпись — название направления из профтеста, хранится id. Раньше id и
    // был подписью: ребёнок читал «creative_arts» вместо «Искусство и
    // культура» (замер 24.09).
    options: [...directions.map((id) => ({ id, label: directionName(id) })), "ни одно, хочу другое"],
    withOther: true,
  };
}

const Q_CAREER_ORIGIN: Question = {
  id: "q_career_origin",
  text: "Кто первым предложил это направление?",
  type: "radio",
  options: ["я сам(а)", "родители", "учитель", "друзья, за компанию", "ещё не выбрал(а)"],
  hint: "Честный ответ здесь важнее правильного — от него зависит, что мы напишем в отчёте.",
};

/**
 * Вопрос о месяце рождения — все три языка в одном месте.
 *
 * АНКЕТА ОДНОЯЗЫЧНАЯ, и это не исключение для одного вопроса: переводов нет
 * ни у одного из её вопросов (см. шапку файла), экран печатает `q.text` как
 * есть. Показать один вопрос по-азербайджански посреди русской анкеты значило
 * бы собрать экран на двух языках. Поэтому экран берёт `ru`, как у всех, а
 * `az` и `en` лежат здесь готовыми — утверждены владельцем 21.09 — и
 * включатся вместе с переводом всей анкеты, а не раньше.
 *
 * ПОДСКАЗКА НЕ ДЛЯ КРАСОТЫ. Семья, которая не понимает, зачем у неё
 * спрашивают возраст ребёнка, либо пропустит вопрос, либо перестанет
 * доверять анкете.
 */
export const BIRTH_MONTH_TEXT = {
  // На «ты», как вся анкета — решение владельца 21.09. Комментарий к колонке
  // в базе остаётся в третьем лице: он для нас, а не для семьи.
  ru: {
    text: "Месяц и год твоего рождения",
    hint:
      "Нужно, чтобы понять, исполнится ли тебе восемнадцать к началу учёбы. Некоторые " +
      "общежития заселяют несовершеннолетних только при опекуне или не заселяют вовсе",
  },
  az: {
    text: "Doğulduğun ay və il",
    hint:
      "Təhsilin başlanğıcına sənin on səkkiz yaşın tamam olub-olmayacağını bilmək üçün " +
      "lazımdır. Bəzi yataqxanalar yetkinlik yaşına çatmayanları yalnız qəyyum ilə " +
      "yerləşdirir və ya ümumiyyətlə yerləşdirmir",
  },
  en: {
    text: "Your month and year of birth",
    hint:
      "We need this to know whether you turn eighteen before studies start. Some halls of " +
      "residence house minors only with a guardian or not at all",
  },
} as const;

export const SCHOOL_SUBJECTS = [
  "математика",
  "физика",
  "химия",
  "биология",
  "информатика",
  "история",
  "литература",
  "иностранный язык",
  "география",
  "обществознание",
  "изобразительное искусство",
  "физкультура",
];

// COUNTRY_OPTIONS удалён: варианты стран живут в COUNTRY_CHOICES
// (answer-values.ts) вместе с кодом ISO, по которому идёт сопоставление.

/**
 * Восемь блоков в порядке из ТЗ. `snapshotDirections` непустой ⇒ первый блок
 * идёт в варианте с q_career_confirm.
 */
export function buildBlocks(
  snapshotDirections: string[] = [],
  /**
   * Страны справочника. Пусто — берётся статический COUNTRY_CHOICES: вопрос
   * без стран хуже любого рассинхрона, семья просто не сможет ответить.
   */
  countryRows: readonly CountryRefRow[] = [],
): Block[] {
  const countries = countryChoices(countryRows);
  const careerQuestions =
    snapshotDirections.length > 0
      ? [careerConfirmQuestion(snapshotDirections), Q_CAREER_ORIGIN]
      : [...BLOCK_1_WITHOUT_SNAPSHOT, Q_CAREER_ORIGIN];

  return [
    {
      id: "block_career",
      title: "Кем хочешь стать",
      questions: careerQuestions,
    },
    {
      id: "block_what",
      title: "Что ищем",
      questions: [
        {
          id: "q_formats",
          text: "Что рассматриваешь?",
          type: "multi",
          options: [...STUDY_FORMATS],
        },
        {
          id: "q_intake_year",
          text: "В каком году планируешь начать учёбу?",
          type: "radio",
          options: [...INTAKE_YEARS],
        },
        {
          id: "q_current_grade",
          text: "В каком ты сейчас классе?",
          type: "radio",
          options: ["8", "9", "10", "11", "уже окончил(а) школу"],
        },
        // МЕСЯЦ И ГОД, А НЕ ДАТА. Дня рождения не храним: он не нужен ни для
        // чего. Вопрос — «исполнится ли восемнадцать к началу учёбы», и месяца
        // для этого хватает. Лишний день — это лишний класс данных о ребёнке.
        //
        // НЕОБЯЗАТЕЛЬНЫЙ. Семья, которая пропустила, доходит до отчёта как
        // раньше. Пустой ответ — третье состояние «возраст неизвестен», а не
        // «совершеннолетний».
        //
        // Сейчас вопрос только собирается: в отчёте он ничего не печатает.
        // Правила общежитий — вторая очередь.
        //
        // Тексты — в BIRTH_MONTH_TEXT на трёх языках, а не литералом здесь.
        {
          id: "q_birth_month",
          text: BIRTH_MONTH_TEXT.ru.text,
          type: "month_year",
          optional: true,
          hint: BIRTH_MONTH_TEXT.ru.hint,
        },
      ],
    },
    {
      id: "block_where",
      title: "Куда",
      questions: [
        {
          id: "q_countries",
          text: "Есть страны, куда хочешь?",
          type: "multi",
          options: [...countries],
          hint: "Ответ „не знаю“ — нормальный. Тогда мы покажем больше вариантов.",
        },
        {
          id: "q_climate",
          text: "Насколько для тебя важно, чтобы было тепло?",
          type: "radio",
          options: ["совсем не важно", "хотелось бы", "важно", "в холод не поеду"],
        },
        {
          id: "q_city_size",
          text: "Где тебе комфортнее?",
          type: "radio",
          options: [
            "большой город",
            "средний город",
            "маленький студенческий городок",
            "не важно",
          ],
        },
        {
          id: "q_safety",
          text: "Насколько для тебя важна безопасность и спокойное отношение к иностранцам?",
          type: "radio",
          options: ["не думал(а) об этом", "важно", "это решающий фактор"],
        },
        {
          id: "q_diaspora",
          text: "Важно, чтобы там были азербайджанцы или знакомые?",
          type: "radio",
          options: [
            "да, важно",
            "хорошо бы, но не обязательно",
            "не важно",
            "наоборот, хочу без своих",
          ],
        },
        {
          id: "q_language_pref",
          text: "На каком языке готов(а) учиться?",
          type: "multi",
          options: STUDY_LANGUAGES,
        },
      ],
    },
    {
      id: "block_have",
      title: "Что у тебя уже есть",
      questions: [
        {
          id: "q_gpa",
          text: "Какой у тебя средний балл?",
          type: "gpa",
          hint: "число или диапазон",
        },
        {
          id: "q_strong_subjects",
          text: "Какие предметы идут лучше всего?",
          type: "multi",
          options: SCHOOL_SUBJECTS,
        },
        {
          id: "q_intl_qualification",
          text: "Учишься по международной программе?",
          type: "radio",
          // ИДЕНТИФИКАТОРЫ, А НЕ ПОДПИСИ. Ответ сопоставляется с колонкой
          // edu_requirements.qualification, и сравнение по подписи сломалось бы
          // от первой же правки копирайтинга — правило проекта с PR #167.
          // Два уже сохранённых ответа переведены миграцией 20260913120000.
          options: SCHOOL_SYSTEMS,
        },
        {
          id: "q_prior_university",
          text: "Учишься или учился(ась) в вузе в Азербайджане?",
          type: "radio",
          options: [...PRIOR_STUDY],
          hint:
            "Один законченный курс дома открывает прямое поступление в Германию и Британию — " +
            "без подготовительного года.",
        },
        {
          id: "q_lang_level",
          text: "Как оцениваешь свой английский?",
          type: "radio",
          options: [...LANGUAGE_LEVELS],
        },
        {
          id: "q_lang_certificate",
          text: "Есть действующий сертификат?",
          type: "radio",
          options: [...LANGUAGE_CERTIFICATES],
        },
        {
          id: "q_lang_score",
          text: "Какой балл?",
          type: "text",
          showIf: hasScoredCertificate,
        },
        {
          id: "q_hsk_level",
          text: "HSK: какой уровень?",
          type: "radio",
          options: [...HSK_LEVELS],
          showIf: isHsk,
        },
        {
          id: "q_hsk_score",
          text: "HSK: балл",
          type: "text",
          optional: true,
          showIf: isHsk,
          hint: "На уровнях 3–6 — из 300, на уровнях 1–2 — из 200. Некоторые вузы просят не только уровень, но и балл.",
        },
        {
          id: "q_lang_listening",
          text: "Аудирование",
          type: "text",
          optional: true,
          showIf: hasCertificate,
        },
        {
          id: "q_lang_reading",
          text: "Чтение",
          type: "text",
          optional: true,
          showIf: hasCertificate,
        },
        {
          id: "q_lang_writing",
          text: "Письмо",
          type: "text",
          optional: true,
          showIf: hasCertificate,
        },
        {
          id: "q_lang_speaking",
          text: "Говорение",
          type: "text",
          optional: true,
          showIf: hasCertificate,
          hint:
            "Британская виза смотрит не общий балл, а каждый раздел отдельно — там нужно " +
            "не ниже 5.5 в каждом.",
        },
        {
          id: "q_lang_date",
          text: "Когда сдавал(а)?",
          type: "month_year",
          showIf: hasCertificate,
          hint: "Сертификаты действуют два года — важно, чтобы он был жив к моменту подачи.",
        },
        {
          id: "q_lang_anxiety",
          text:
            "Насколько тебе тревожно говорить и писать на этом языке с незнакомыми людьми?",
          type: "radio",
          options: ["спокойно", "немного волнуюсь", "сильно волнуюсь", "избегаю таких ситуаций"],
        },
        {
          id: "q_exams_taken",
          text: "Сдавал(а) какие-то экзамены для поступления?",
          type: "multi",
          options: [...ENTRANCE_EXAMS],
        },
        // По одному полю на экзамен: из общей строки «SAT 1350, IELTS 7» движок
        // мог достать только факт «сдавал», а не балл, и порог не проверялся.
        {
          // Балл ДИМ и ГРУППА — одним вопросом в две строки: порознь они
          // бесполезны. Балл без группы сравнивать не с чем (614,6 из 700 и
          // 60,0 из 100 — разные шкалы), группа без балла ничего не говорит.
          id: "q_score_dim",
          text: "ДИМ: балл",
          type: "text",
          optional: true,
          showIf: examTaken("dim"),
        },
        {
          id: "q_dim_group",
          text: "ДИМ: какая группа",
          type: "radio",
          options: DIM_GROUPS,
          optional: true,
          showIf: examTaken("dim"),
        },
        {
          id: "q_score_sat",
          text: "SAT: общий балл",
          type: "text",
          optional: true,
          showIf: examTaken("sat"),
        },
        {
          // Отдельная строка не прихоть: İTÜ требует 1200 общий ПРИ 650 по
          // математике, и без разбивки такой порог не проверить вовсе.
          id: "q_score_sat_math",
          text: "SAT: балл по математике",
          type: "text",
          optional: true,
          showIf: examTaken("sat"),
        },
        {
          id: "q_score_act",
          text: "ACT: составной балл",
          type: "text",
          optional: true,
          showIf: examTaken("act"),
        },
        {
          id: "q_score_yos",
          text: "TR-YÖS: балл",
          type: "text",
          optional: true,
          showIf: examTaken("tr_yos"),
        },
        {
          id: "q_score_testas",
          text: "TestAS: балл",
          type: "text",
          optional: true,
          showIf: examTaken("testas"),
        },
        {
          id: "q_score_ucat",
          text: "UCAT: балл",
          type: "text",
          optional: true,
          showIf: examTaken("ucat"),
        },
      ],
    },
    {
      id: "block_money",
      title: "Про деньги",
      subtitle:
        "Это лучше уточнить у родителей — можно позвать их прямо сейчас или пропустить и " +
        "вернуться позже.",
      skippable: true,
      skipLabel: "Пропустить этот блок",
      questions: [
        {
          id: "q_budget_range",
          text:
            "Сколько семья готова тратить на учёбу в год — со всем: обучение, жильё, еда, перелёты?",
          type: "radio",
          options: [...BUDGET_RANGES],
        },
        // ВОПРОС ПРО ВАЛЮТУ УБРАН, И ЭТО НЕ УПРОЩЕНИЕ.
        //
        // Вилки бюджета выше — долларовые с самого начала: «до 10 тысяч
        // долларов», ceilingUsd. Ответ про валюту менял только то, в чём
        // считалась стоимость, а потолок оставался долларовым — семья,
        // ответившая «манаты», получала сравнение манатов с долларами. Из двух
        // живых анкет одна именно такая.
        //
        // Вопрос, который не может ничего изменить правильно, — это вопрос,
        // который не задают. Сверка теперь всегда в долларах
        // (BUDGET_COMPARISON_CURRENCY), а цена печатается в валюте страны, как
        // и печаталась: счёт вуз выставляет не в долларах.
        // ВОПРОС «ЧТО УЖЕ ПОСЧИТАНО В ЭТОЙ СУММЕ» УБРАН, И ЭТО НЕ УПРОЩЕНИЕ.
        //
        // Он спрашивался и не читался НИКЕМ: слово q_budget_includes
        // встречалось только здесь — ни в profile-to-family, ни в движке.
        // Восемь галочек у семьи, ответ в никуда.
        //
        // Убран, а не подключён, по той же причине, по какой убран вопрос про
        // валюту: сверка с бюджетом идёт по полной стоимости года, и состав
        // семейной суммы её не меняет. Читать ответ значило бы вычитать из
        // цены то, что семья «уже посчитала», — то есть строить число на
        // галочке, которую никто не проверял.
        //
        // Полнота суммы теперь обеспечена с ДРУГОЙ стороны: жильё добирается
        // из страновой строки, если вуз его не назвал. Вопрос анкеты этого
        // сделать не мог.
        {
          id: "q_scholarship_ready",
          text: "Готовы потратить лишний год на подготовку, если это даст стипендию?",
          type: "radio",
          options: ["да", "нет, время важнее", "зависит от суммы", "не знаю"],
        },
      ],
    },
    {
      id: "block_ready",
      title: "Готовность",
      questions: [
        {
          id: "q_prior_independence",
          text:
            "Уезжал(а) когда-нибудь один на долгое время — лагерь, обмен, поездка без родителей?",
          type: "radio",
          options: ["да, и не раз", "один раз", "нет, никогда"],
        },
        {
          id: "q_main_worry",
          text: "Что беспокоит больше всего в переезде?",
          type: "multi",
          options: MAIN_WORRY_CHOICES,
          withOther: true,
        },
        {
          id: "q_daily_autonomy",
          text:
            "Кто обычно решает твои бытовые вопросы — записаться к врачу, оплатить что-то, " +
            "разобраться с документами?",
          type: "radio",
          options: ["я сам(а)", "чаще родители", "всегда родители"],
        },
        {
          id: "q_parents_ready",
          text: "Как родители относятся к отъезду?",
          type: "radio",
          options: [
            "полностью поддерживают",
            "согласны, но переживают",
            "сомневаются",
            "против, но я хочу",
            "ещё не обсуждали",
          ],
        },
      ],
    },
    {
      id: "block_limits",
      title: "Ограничения",
      subtitle: "Эти вещи отсекают варианты сильнее, чем деньги — лучше сказать сразу.",
      questions: [
        {
          id: "q_health",
          text: "Есть что-то со здоровьем, что нужно учесть при выборе страны?",
          type: "radio",
          options: [...HEALTH_CHOICES],
          withOther: true,
        },
        {
          id: "q_diet",
          text: "Есть требования к питанию?",
          type: "multi",
          options: [...DIET_CHOICES],
          withOther: true,
        },
        {
          id: "q_housing",
          text: "Есть требования к жилью — например, раздельное проживание?",
          type: "radio",
          options: [...HOUSING_CHOICES],
          withOther: true,
        },
        {
          id: "q_excluded_countries",
          text: "Есть страны, куда точно не поедешь?",
          type: "multi",
          options: countries.filter((c) => c.id !== "show_me"),
          withOther: true,
        },
      ],
    },
    {
      id: "block_plan_b",
      title: "План Б",
      questions: [
        {
          id: "q_plan_b",
          text: "Что будешь делать, если не получится поступить туда, куда хочешь?",
          type: "radio",
          options: [
            "поступлю дома и попробую через год",
            "поеду туда, куда получится",
            "останусь учиться дома",
            "не думал(а) об этом",
          ],
          hint: "Ответ „не думал“ — тоже ответ. Тогда мы предложим план в отчёте.",
        },
        // ВОПРОС ПРО ЯЗЫК ДОКУМЕНТА СНЯТ ДО ПЕРЕВОДА ПРОЗЫ, А НЕ ЗАБЫТ.
        //
        // Переключается пока только справочник: названия, цены, пометки, даты.
        // Наша собственная проза — 274 строки, около полутора тысяч слов в
        // report-texts.ts — остаётся русской. Семья, выбравшая «azərbaycan
        // dili», получила бы азербайджанские имена и цифры внутри русских
        // заголовков: документ, который выглядит сломанным, а не переведённым.
        //
        // Форма, обещающая больше, чем держит содержание, — тот же тихий
        // обман, что и пустой порог, прочитанный как «хватает».
        //
        // ВЕСЬ МЕХАНИЗМ ЖИВ: pick, колонки загрузчика, сортировки по
        // напечатанной подписи, чтение answers.q_report_language в
        // profile-to-family. Вернуть вопрос — вставить сюда блок с
        // DOC_LANGUAGE_CHOICES и поднять версию банка. Прогнать отчёт на
        // азербайджанском можно и сейчас: document_language в профиле.
      ],
    },
  ];
}

/** Вопросы блока, которые нужно показать при текущих ответах. */
export function visibleQuestions(block: Block, answers: Answers): Question[] {
  return block.questions.filter((q) => !q.showIf || q.showIf(answers));
}

/** Есть ли ответ: пустая строка, пробелы и пустой список ответом не считаются. */
export function isAnswered(value: AnswerValue | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some((v) => typeof v === "string" && v.trim() !== "");
  return value.trim() !== "";
}

/**
 * ОБЯЗАТЕЛЬНЫЕ ВОПРОСЫ БЕЗ ОТВЕТА — одна проверка для экрана и сервера.
 *
 * Гостевая анкета пускала «Дальше» с пустым блоком, и отчёт строился на
 * пустых ответах (аудит 12.10). Обязателен показанный вопрос без optional.
 * Блок, пропущенный кнопкой (деньги), не проверяется — пропуск и есть ответ.
 */
export function missingRequired(block: Block, answers: Answers): string[] {
  return visibleQuestions(block, answers)
    .filter((q) => !q.optional && !isAnswered(answers[q.id]))
    .map((q) => q.id);
}

/** Первый блок с пустым обязательным вопросом и сами вопросы; null — всё отвечено. */
export function firstMissingRequired(
  blocks: Block[],
  answers: Answers,
  budgetSkipped: boolean,
): { blockIndex: number; questionIds: string[] } | null {
  for (let i = 0; i < blocks.length; i++) {
    if (budgetSkipped && blocks[i].skippable) continue;
    const ids = missingRequired(blocks[i], answers);
    if (ids.length) return { blockIndex: i, questionIds: ids };
  }
  return null;
}

// Винительный падеж для эха. Списки закрытые, поэтому это таблица, а не
// правило: «рассматриваешь Германия и Британия» — то, что человек сразу
// замечает и после чего перестаёт верить остальному тексту. Полноценная
// морфология ради двух фраз была бы дороже и всё равно ошибалась на именах
// вроде «ОАЭ».
const ACCUSATIVE: Record<string, string> = {
  Турция: "Турцию",
  Германия: "Германию",
  Британия: "Британию",
  США: "США",
  ОАЭ: "ОАЭ",
  другие: "другие",
  бакалавриат: "бакалавриат",
  "подготовительный год перед вузом": "подготовительный год перед вузом",
  "летняя школа": "летнюю школу",
  "школа-пансион": "школу-пансион",
  "языковой курс": "языковой курс",
};

const acc = (v: string) => ACCUSATIVE[v] ?? v;

/**
 * Эхо ответов — пересказ, а НЕ вывод. «Ты ищешь бакалавриат, старт в 2027,
 * рассматриваешь Германию и Британию».
 *
 * Никаких промежуточных оценок достижимости: на неполных данных они врут, а
 * исправленный в конце вывод читается как обман. Поэтому здесь только то, что
 * человек уже сказал, своими же словами.
 */
export function answersEcho(answers: Answers): string | null {
  const parts: string[] = [];

  const formats = labelsOf(STUDY_FORMATS, answers.q_formats).filter(
    (_, i) => toId(STUDY_FORMATS, (answers.q_formats as string[])[i]) !== "unsure",
  );
  if (formats.length) parts.push(`Ты ищешь ${formats.map(acc).join(", ")}`);

  const year = optionOf(INTAKE_YEARS, answers.q_intake_year);
  if (year?.cycle) parts.push(`старт в ${year.label}`);

  const named = Array.isArray(answers.q_countries)
    ? (answers.q_countries as string[])
        .filter((c) => toId(COUNTRY_CHOICES, c) !== "show_me")
        .map((c) => labelOf(COUNTRY_CHOICES, c))
        .filter(Boolean) as string[]
    : [];
  if (named.length) parts.push(`рассматриваешь ${named.map(acc).join(" и ")}`);

  // Ответ — идентификатор, эхо — пересказ СВОИМИ СЛОВАМИ. Здесь печаталось
  // сохранённое значение как есть, и семья читала «язык обучения — tr».
  // Единственная строка эха, где подпись не бралась из таблицы: остальные
  // четыре уже шли через labelsOf/labelOf.
  const langs = labelsOf(STUDY_LANGUAGES, answers.q_language_pref);
  if (langs.length) {
    parts.push(`язык обучения — ${langs.join(", ")}`);
  }

  if (!parts.length) return null;
  return `${parts.join(", ")}.`;
}

/** Что уходит в отдельные колонки edu_profiles (см. конец ТЗ). */
export function columnsFromAnswers(answers: Answers) {
  const arr = (v: AnswerValue) => (Array.isArray(v) ? v : v ? [String(v)] : []);
  const str = (v: AnswerValue) => (typeof v === "string" && v !== "" ? v : null);
  const monthYearToDate = (v: AnswerValue) => {
    // «2026-05» → первое число месяца; сертификат живёт два года, дня хватает.
    const s = str(v);
    if (!s) return null;
    return /^\d{4}-\d{2}$/.test(s) ? `${s}-01` : null;
  };
  // Месяц рождения хранится КАК ЕСТЬ, «2009-11», без дня. В отличие от даты
  // сертификата, день здесь не просто не нужен — его нельзя выдумывать:
  // первое число месяца в колонке типа date читалось бы как день рождения.
  return {
    intake_year: str(answers.q_intake_year),
    formats: arr(answers.q_formats),
    countries: arr(answers.q_countries),
    study_languages: arr(answers.q_language_pref),
    language_level: str(answers.q_lang_level),
    language_certificate: str(answers.q_lang_certificate),
    // У HSK балл сертификата — номер уровня из q_hsk_level (1–6), см.
    // HSK_LEVELS. Ответ q_lang_score, оставшийся от прежнего выбора
    // сертификата, сюда не попадает.
    language_score:
      toId(LANGUAGE_CERTIFICATES, answers.q_lang_certificate) === HSK_CODE
        ? (hskLevelOf(answers.q_hsk_level)?.toString() ?? null)
        : str(answers.q_lang_score),
    lang_listening: str(answers.q_lang_listening),
    lang_reading: str(answers.q_lang_reading),
    lang_writing: str(answers.q_lang_writing),
    lang_speaking: str(answers.q_lang_speaking),
    prior_university: str(answers.q_prior_university),
    exam_score_sat: str(answers.q_score_sat),
    exam_score_sat_math: str(answers.q_score_sat_math),
    exam_score_act: str(answers.q_score_act),
    exam_score_yos: str(answers.q_score_yos),
    exam_score_testas: str(answers.q_score_testas),
    exam_score_ucat: str(answers.q_score_ucat),
    // Балл и группа ДИМ. Колонки заведены 20260911120000, но их не писал
    // никто, и балл из анкеты до движка не доезжал. Балл — только число в
    // пределах шкалы названной группы (dimScoreOf), иначе NULL.
    exam_score_dim: dimScoreOf(answers.q_score_dim, dimGroupOf(answers.q_dim_group)),
    dim_group: dimGroupOf(answers.q_dim_group),
    language_exam_date: monthYearToDate(answers.q_lang_date),
    birth_month: birthMonthOf(answers.q_birth_month),
    gpa: str(answers.q_gpa),
    budget_range: str(answers.q_budget_range),
    budget_currency: str(answers.q_budget_currency),
  };
}

// ── отпечаток банка вопросов ──────────────────────────────────────────
//
// ЗАЧЕМ. Версия банка (EDULINK_QUESTIONNAIRE_VERSION) кладётся в снимок
// отчёта, чтобы через год было видно, из какой анкеты он собран. Но версия
// проставляется рукой, а дисциплина истирается — сегодня же мы это доказали
// на зонде, который три дня пролежал в чужой почте.
//
// Отпечаток — детектор забывчивости, и только он. В снимок он НЕ идёт:
// консультанту, открывшему бриф через год, шестнадцать шестнадцатеричных
// знаков не говорят ничего, а «edulink-2026-08» говорит.
//
// СЧИТАЕТСЯ ПО СТРУКТУРЕ, НЕ ПО ПОДПИСЯМ. Тексты вопросов и подписи вариантов
// переписываются при вычитке, и падать на каждой запятой корректора значит
// приучить всех обновлять версию не глядя. Меняется же по существу другое:
// набор вопросов, их порядок, тип, обязательность, наличие «другого»,
// условие показа и — главное — идентификаторы вариантов, по которым считает
// движок.

function fingerprintOfQuestion(q: Question): string {
  const optionIds = (q.options ?? []).map((o) => (typeof o === "string" ? "?" : o.id));
  return [
    q.id,
    q.type,
    q.optional ? "opt" : "req",
    q.withOther ? "other" : "-",
    q.showIf ? "cond" : "-",
    optionIds.join("|"),
  ].join(":");
}

/**
 * Структура банка вопросов одной строкой. Сравнивается с
 * EDULINK_QUESTIONNAIRE_FINGERPRINT в prebuild.
 *
 * Блоки берутся в обоих видах — со снимком карьерного теста и без него, —
 * потому что от снимка зависит первый блок, и правка только в одной из веток
 * иначе прошла бы мимо.
 */
export function questionnaireStructure(): string {
  const parts: string[] = [];
  for (const [label, blocks] of [
    ["no-snapshot", buildBlocks([])],
    ["with-snapshot", buildBlocks(["it", "med"])],
  ] as const) {
    parts.push(`#${label}`);
    for (const b of blocks) {
      parts.push(`[${b.id}]`);
      for (const q of b.questions) parts.push(fingerprintOfQuestion(q));
    }
  }
  return parts.join("\n");
}
